import { useState, useEffect } from "react";
import {
  useLoaderData,
  useParams,
  Link,
  Form,
  useNavigation,
  useActionData,
  useFetcher,
  useRevalidator,
} from "react-router-dom";
import { toast } from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import { Header } from "../../components/layout/Header";
import { TeamMemberList } from "../../components/teams/TeamMemberList";
import { Button, LinkButton } from "../../components/common";
import type { TeamDetailData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import type { Role } from "../../types/user";
import styles from "./TeamDetailPage.module.css";

export default function TeamDetailPage() {
  const { id: orgId, teamId } = useParams<{ id: string; teamId: string }>();
  const { team, members } = useLoaderData() as TeamDetailData;
  const actionData = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const { user } = useAuth();
  const deleteFetcher = useFetcher<ActionResult>();
  const removeMemberFetcher = useFetcher<ActionResult>();
  const revalidator = useRevalidator();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);

  const userRole: Role | undefined = user?.organizations.find((org) => org.id === orgId)?.role;

  const canManageTeams = userRole === "owner" || userRole === "admin";
  const isSubmitting = navigation.state === "submitting";
  const isDeleting = deleteFetcher.state === "submitting";

  useEffect(() => {
    if (actionData && navigation.state === "idle") {
      if (actionData.success) {
        toast.success("Team updated successfully");
        setShowEditForm(false);
      } else if (actionData.error) {
        toast.error(actionData.error);
      }
    }
  }, [actionData, navigation.state]);

  useEffect(() => {
    if (deleteFetcher.data && deleteFetcher.state === "idle") {
      if (!deleteFetcher.data.success && deleteFetcher.data.error) {
        toast.error(deleteFetcher.data.error);
      }
    }
  }, [deleteFetcher.data, deleteFetcher.state]);

  useEffect(() => {
    if (removeMemberFetcher.data && removeMemberFetcher.state === "idle") {
      if (removeMemberFetcher.data.success) {
        toast.success("Member removed from team");
        revalidator.revalidate();
      } else if (removeMemberFetcher.data.error) {
        toast.error(removeMemberFetcher.data.error);
      }
      setRemovingMemberId(null);
    }
  }, [removeMemberFetcher.data, removeMemberFetcher.state, revalidator]);

  const handleDeleteTeam = () => {
    deleteFetcher.submit(null, {
      method: "post",
      action: `/organizations/${orgId}/teams/${teamId}/delete`,
    });
  };

  const handleRemoveMember = (userId: string, email: string) => {
    if (!confirm(`Are you sure you want to remove ${email} from this team?`)) {
      return;
    }
    setRemovingMemberId(userId);
    removeMemberFetcher.submit(null, {
      method: "post",
      action: `/organizations/${orgId}/teams/${teamId}/members/${userId}/remove`,
    });
  };

  return (
    <>
      <Header />
      <div className={styles.container}>
        <Link to={`/organizations/${orgId}/teams`} className={styles.backLink}>
          Back to Teams
        </Link>

        {showEditForm ? (
          <Form method="post" className={styles.editForm}>
            <div className={styles.formGroup}>
              <label htmlFor="name" className={styles.label}>
                Team Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                defaultValue={team.name}
                required
                minLength={2}
                maxLength={100}
                className={styles.input}
              />
              {actionData?.fieldErrors?.name && (
                <p className="error-text">{actionData.fieldErrors.name}</p>
              )}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="description" className={styles.label}>
                Description
              </label>
              <textarea
                id="description"
                name="description"
                defaultValue={team.description || ""}
                maxLength={500}
                rows={3}
                className={styles.textarea}
              />
              {actionData?.fieldErrors?.description && (
                <p className="error-text">{actionData.fieldErrors.description}</p>
              )}
            </div>
            <div className={styles.buttonGroup}>
              <Button
                type="submit"
                variant="primary"
                loading={isSubmitting}
                loadingText="Saving..."
              >
                Save Changes
              </Button>
              <Button type="button" variant="secondary" onClick={() => setShowEditForm(false)}>
                Cancel
              </Button>
            </div>
          </Form>
        ) : (
          <div className={styles.header}>
            <div>
              <h1 className={styles.title}>{team.name}</h1>
              {team.description && <p className={styles.description}>{team.description}</p>}
            </div>
            {canManageTeams && (
              <Button variant="primary" onClick={() => setShowEditForm(true)}>
                Edit Team
              </Button>
            )}
          </div>
        )}

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Team Members</h2>
            {canManageTeams && (
              <LinkButton
                to={`/organizations/${orgId}/teams/${teamId}/members/add`}
                variant="primary"
                size="sm"
              >
                Add Member
              </LinkButton>
            )}
          </div>

          <div className={styles.membersContainer}>
            <TeamMemberList
              members={members}
              currentUserRole={userRole!}
              onRemoveMember={handleRemoveMember}
              removingMemberId={removingMemberId}
            />
          </div>
          {members.length === 0 && canManageTeams && (
            <div className={styles.emptyAction}>
              <LinkButton
                to={`/organizations/${orgId}/teams/${teamId}/members/add`}
                variant="primary"
                size="sm"
              >
                Add First Member
              </LinkButton>
            </div>
          )}
        </div>

        {canManageTeams && (
          <div className={styles.dangerZone}>
            <h3 className={styles.dangerTitle}>Danger Zone</h3>
            <p className={styles.dangerText}>
              Once you delete a team, there is no going back. All team members will be removed.
            </p>
            {showDeleteConfirm ? (
              <div>
                <p className={styles.confirmText}>Are you sure you want to delete this team?</p>
                <div className={styles.buttonGroup}>
                  <Button
                    variant="danger"
                    onClick={handleDeleteTeam}
                    loading={isDeleting}
                    loadingText="Deleting..."
                  >
                    Yes, Delete Team
                  </Button>
                  <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
                Delete Team
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
